import React, {useEffect, useRef, useState} from "react";
import "./App.css";
import Content from "./content-interface";
import ContentOverview from "./content-overview-interface";
import Question from "./components/question/Question";
import Intro from "./components/intro/Intro";
import QuestionNavigation from "./components/questionNavigation/QuestionNavigation";
import Report from "./components/report/Report";

const App = ({
  contentOverviewPath
}: {
  contentOverviewPath: string
}) => {
  const currStepQueryParamName = 'step';
  const answersQueryParamName = 'answers';
  const firstMount = useRef(true);
  const [currStep, setCurrStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<Content | null>(null);
  const [answers, setAnswers] = useState<{ [key: number]: boolean }>({});

  // Load content data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const overviewHeaders = new Headers();
        overviewHeaders.append('pragma', 'no-cache');
        overviewHeaders.append('cache-control', 'no-cache');
        const resOverview = await fetch(contentOverviewPath, {headers: overviewHeaders});
        const overview: ContentOverview = await resOverview.json();

        const searchParams = new URLSearchParams(window.location.search);
        const latestVersionKey = searchParams.has('v') && overview[searchParams.get('v') as string] ? searchParams.get('v') as string : Object.keys(overview || {})
          .sort((a, b) => a.localeCompare(b, undefined, {numeric: true}))
          .pop();

        if (latestVersionKey) {
          if (latestVersionKey !== searchParams.get('v')) {
            searchParams.set('v', latestVersionKey);
            window.history.replaceState(undefined, '', `${window.location.pathname}?${searchParams.toString()}`);
          }
          const resContent = await fetch(overview[latestVersionKey]);
          const content = await resContent.json();
          setContent(content);
        } else {
          throw new Error('No content version found in overview file');
        }
      } catch (err: any) {
        setError("Content in this version couldn't be loaded");
      }
    }
    fetchData();
  }, [contentOverviewPath]);

  //Load answers query params if this is the first mount
  useEffect(() => {
    if (firstMount.current) {
      loadStateFromQueryParams()
      firstMount.current = false;
    }
  }, []);

  // Set answers query params
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      const queryParams = new URLSearchParams(window.location.search);
      const value = Object.values(answers).reduce((str, answer) => str + (answer ? '1' : '0'), '');
      if (queryParams.get(answersQueryParamName) !== value) {
        queryParams.set(answersQueryParamName, value);
        window.history.replaceState(undefined, '', `${window.location.pathname}?${queryParams.toString()}`)
      }
    }
  }, [answers]);

  // Set step query param
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (parseInt(queryParams.get(currStepQueryParamName) || '', 10) !== currStep) {
      queryParams.set(currStepQueryParamName, currStep.toString());
      window.history.pushState(undefined, '', `${window.location.pathname}?${queryParams.toString()}`);
    }
  }, [currStep]);

  // Listen for query param changes
  useEffect(() => {
    window.addEventListener('popstate', loadStateFromQueryParams);
    return () => {
      window.removeEventListener('popstate', loadStateFromQueryParams);
    };
  }, []);

  const loadStateFromQueryParams = () => {
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.has(answersQueryParamName)) {
      setAnswers(
        (queryParams.get(answersQueryParamName) as string).split('').reduce((acc, answer, i) => ({
          ...acc,
          [i + 1]: answer === '1'
        }), {}));
    } else {
      setAnswers({});
    }

    if (queryParams.has(currStepQueryParamName)) {
      setCurrStep(parseInt(queryParams.get(currStepQueryParamName) as string, 10) || 0);
    }
  }

  const nextStep = () => {
    if (content?.questions[currStep - 1] && content?.questions[currStep - 1].conflictingPrinciples === 'all' && answers[currStep] !== undefined && !answers[currStep]) { // Special case for knockout-questions
      setCurrStep(-1);
    } else {
      if (currStep === (content?.questions || []).length) {
        setCurrStep(-1);
      } else {
        if (currStep === 3 && answers[currStep] !== undefined && !answers[currStep]) {
          setAnswers(answers => ({...answers, 4: false}));
          setCurrStep(5);
        } else {
          setCurrStep(currStep => currStep + 1);
        }
      }
    }
  }

  const prevStep = () => {
    setCurrStep(currStep => currStep - 1);
  }

  const answerQuestion = (value: boolean) => {
    if (value === answers[currStep])
      return;

    const newAnswers: { [key: number]: boolean } = {};
    [...Array(currStep)].forEach((_, i) => {
      if (typeof answers[i + 1] === 'undefined')
        newAnswers[i + 1] = true;
    })

    setAnswers(answers => ({...answers, ...newAnswers, [currStep]: value}));
  }

  const getPrinciplesForQuestion = (question: Content['questions'][number]): Content['principles'] | string => {
    if (typeof question?.conflictingPrinciples === 'string') {
      return question.conflictingPrinciples;
    }

    return (question?.conflictingPrinciples || [])
      .reduce((acc, id) => {
        if ((content?.principles || {})[id.toString()])
          return {...acc, [id]: content?.principles[id.toString()]}

        return acc;
      }, {});
  }

  const renderError = () => <div className="error">{error}</div>;

  return (
    <div className="App">
      <img className="gfxc_logo" src="/img/GFXlogo.png" alt="Global Foreign Exchange Committee"/>
      <h2>Proportionality Self-Assessment Tool</h2>
      {error ? renderError() : content && <>
        {currStep === 0 && <Intro
          intro={content?.intro}
          setCurrStep={setCurrStep}
          setError={setError}/>}
        {currStep !== 0 && <QuestionNavigation
          questions={content?.questions}
          currStep={currStep}
          setCurrStep={setCurrStep}/>}
        {currStep > 0 && <Question
          question={content?.questions[currStep - 1] || []}
          currAnswer={answers[currStep]}
          answerQuestion={answerQuestion}
          principles={getPrinciplesForQuestion(content?.questions[currStep - 1])}
          setError={setError}
          nextStep={nextStep}
          prevStep={prevStep}/>}
        {currStep === -1 && <Report
          principles={content?.principles}
          principleCategories={content?.principleCategories}
          questions={content?.questions}
          answers={answers}
          fullFxDownloadLink={content?.full_fx_global_code_download_link}
          setCurrStep={setCurrStep}/>}
      </>}
    </div>
  );
}

export default App;
